import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import * as _ from 'lodash';
import { Combination } from 'js-combinatorics';
import { login, fetchUser } from '../auth';
import { getBetSlipMatchesReq, calculateBetBuilderFilterReq } from './services';
import oddsTransformer from 'toolkit/oddsTransformer';
import { checkIfLiveEvent, isHandicap90Specifier } from 'toolkit/utils';
import {
  BET_BUILDER_STATUS,
  betBuilderStatusFromError,
  buildBetBuilderRequest
} from 'toolkit/betBuilder';

const initialState = {
  outright: 0,
  total: 10,
  defaultStake: 10,
  bets: [],
  system: false,
  combinations: [],
  maxWinning: 0,
  tax: 0,
  taxRate: 0,
  stakePerBet: 0,
  numberOfBets: 0,
  isBetSlipSubmitting: false,
  resultModalShow: false,
  responseData: {
    success: false,
    text: ''
  },
  betBuilder: {
    isEnabled: false,
    selections: [],
    showModal: false,
    calculatedOdds: null,
    status: BET_BUILDER_STATUS.IDLE,
    // "marketId|specifiers|outcomeId" of outcomes that cannot be added to the current selection
    conflicts: [],
    requestId: null
  }
};

export const getBetSlipMatches = createAsyncThunk('betSlip/getBetSlipMatches', async () => {
  try {
    const betSlips = localStorage.getItem('betSlipData');
    if (betSlips) {
      const betSlipObject = JSON.parse(betSlips);
      if (betSlipObject?.bets?.length === 0) return null;
      const body = betSlipObject?.bets?.map((b) => ({
        eventId: b.providerId,
        marketId: b.marketStringId,
        outcomeId: b.outcomeId,
        banker: b.banker,
        odds: b.odds
      }));
      const response = await getBetSlipMatchesReq({
        outcomes: body
      });
      return response.map((m) => {
        return { ...m, markets: m.markets.filter((m) => m.outcomes.length > 0) };
      });
    }
    return null;
  } catch (error) {
    return null;
  }
});

// Prices the bet builder selection through MyOdds and learns which further outcomes would conflict.
// Rejects with a BET_BUILDER_STATUS value.
export const calculateBetBuilderOdds = createAsyncThunk(
  'betSlip/calculateBetBuilderOdds',
  async (selections, { getState, rejectWithValue }) => {
    if (!selections || selections.length === 0) {
      return null;
    }

    const providerId = selections[0].providerId;
    const offeredMarkets =
      getState().match.betBuilderAvailableSelections?.[providerId]?.data?.markets;
    const body = buildBetBuilderRequest(selections, offeredMarkets);
    if (!body) {
      return rejectWithValue(BET_BUILDER_STATUS.NOT_COMBINABLE);
    }

    try {
      const response = await calculateBetBuilderFilterReq(body);
      return {
        calculatedOdds: response?.data?.calculatedOdds ?? null,
        conflicts: response?.data?.filteredOutcomes ?? []
      };
    } catch (error) {
      return rejectWithValue(betBuilderStatusFromError(error));
    }
  }
);

const resetBetBuilderCalculation = (betBuilder) => {
  betBuilder.calculatedOdds = null;
  betBuilder.conflicts = [];
  betBuilder.status = BET_BUILDER_STATUS.IDLE;
  betBuilder.requestId = null;
};

export const checkEventBetStatus_UpdateMsg = (payload) => (dispatch, getState) => {
  const { bets } = getState().betSlip;
  const events = payload.events;
  events.forEach((event) => {
    const filteredBetList = bets.filter((b) => b.providerId === event.gameNumber);
    if (filteredBetList.length > 0) {
      filteredBetList.forEach((bet) => {
        try {
          const filteredMarket = event.markets.find((m) => m.stringId === bet.marketStringId);

          const updatedOutcome = filteredMarket.outcomes.find((o) => o.id === bet.outcomeId);

          if (updatedOutcome) {
            const updatedBetData = {
              providerId: bet.providerId,
              marketStringId: bet.marketStringId,
              outcomeId: bet.outcomeId,
              enabled: updatedOutcome.enabled && !event.placeBetsNotAllowed,
              odds: updatedOutcome.odds,
              match: event
            };

            dispatch(updateBetSlipBetsOdds(updatedBetData));
          }
        } catch (error) {
          // console.log('error', error);
        }
      });
    }
  });
};

export const checkEventBetStatus_DeleteMsg = (payload) => (dispatch, getState) => {
  const { bets } = getState().betSlip;

  const gameNumbers = payload.events;

  gameNumbers.forEach((gameNumber) => {
    bets.forEach((bet) => {
      if (bet.providerId === gameNumber) {
        dispatch(removeBetFromBetSlip(bet));
      }
    });
  });
};

const _oddsTransformer = oddsTransformer();

const calculateMaxWinningAndPrepareBetSlip = (state) => {
  saveBetSlipToLocalStorage(state);
  const outcomeMaps = {};
  
  // Process all enabled bets - normal bets work exactly as before
  const enabledBets = state.bets.filter((b) => b.enabled);
  
  // Separate bet builder bets from normal bets only if there are bet builder bets
  const betBuilderBets = enabledBets.filter((b) => b.isBetBuilder === true);
  const normalBets = enabledBets.filter((b) => b.isBetBuilder !== true);
  
  // Group bet builder bets by BetBuilderId (only if there are any)
  const betBuilderGroups = betBuilderBets.length > 0 ? _.groupBy(betBuilderBets, 'betBuilderId') : {};
  
  // Process normal bets exactly as before (original logic)
  normalBets.forEach((b) => {
    if (outcomeMaps[b.providerId] === undefined) outcomeMaps[b.providerId] = [];

    const isMaradonaHandicap = isHandicap90Specifier(b.marketStringId);
    const effectiveMultiplier = isMaradonaHandicap ? 1.0 : state.customOddsMultiplier;

    outcomeMaps[b.providerId].push({
      providerId: b.providerId,
      marketStringId: b.marketStringId,
      outcomeId: b.outcomeId,
      referenceOdds: b.odds,
      odds: _oddsTransformer.transform(
        new Date(),
        checkIfLiveEvent(b.match),
        b.match?.isSeason === true,
        b.marketId,
        new Date(b.match.gameDate),
        b.odds * effectiveMultiplier
      )
    });
  });
  
  // Process bet builder bets - treat each group as a single bet (only if there are any)
  Object.keys(betBuilderGroups).forEach((betBuilderId) => {
    const groupBets = betBuilderGroups[betBuilderId];
    const firstBet = groupBets[0];
    const providerId = firstBet.providerId;
    
    // Only MyOdds prices a custom bet; single odds of correlated selections cannot be multiplied
    const calculatedOdds = firstBet.betBuilderCalculatedOdds ?? 0;
    
    // Apply customOddsMultiplier to calculated odds for max winning calculation
    
    const groupHasMaradonaHandicap = groupBets.some((bet) =>
      isHandicap90Specifier(bet.marketStringId)
    );
    const effectiveMultiplier = groupHasMaradonaHandicap ? 1.0 : state.customOddsMultiplier;
    // Use raw calculated odds * effectiveMultiplier to preserve exact value (e.g., 10.86)
    const oddsForCalculation = calculatedOdds * effectiveMultiplier;
    
    if (outcomeMaps[providerId] === undefined) outcomeMaps[providerId] = [];
    // Use the first bet's details as representative, but with calculated odds
    // Store raw odds for max winning calculation to preserve exact API value
    outcomeMaps[providerId].push({
      providerId: providerId,
      marketStringId: firstBet.marketStringId,
      outcomeId: firstBet.outcomeId,
      referenceOdds: calculatedOdds,
      odds: oddsForCalculation, // Use raw calculated odds * multiplier (preserves 10.86)
      isBetBuilder: true,
      betBuilderId: parseInt(betBuilderId)
    });
  });

  // Count unique matches - if no bet builder bets, use original logic
  let mCount;
  if (betBuilderBets.length === 0) {
    // Original logic for normal bets only
    mCount = _.uniqBy(
      enabledBets.filter((b) => !b.banker),
      'providerId'
    ).length;
  } else {
    // Mixed or bet builder only - treat bet builder groups as single bets
    const uniqueNormalBets = _.uniqBy(
      normalBets.filter((b) => !b.banker),
      'providerId'
    );
    const uniqueBetBuilderGroups = Object.keys(betBuilderGroups).filter((betBuilderId) => {
      const groupBets = betBuilderGroups[betBuilderId];
      return !groupBets.some((b) => b.banker);
    }).length;
    mCount = uniqueNormalBets.length + uniqueBetBuilderGroups;
  }

  if (state.system) {
    let allLegs = [];

    // If no bet builder bets, use original logic
    if (betBuilderBets.length === 0) {
      // Original system bet logic
      const uniqueBetsExceptBanker = _.uniqBy(
        enabledBets.filter((b) => !b.banker),
        'providerId'
      );
      state.combinations.forEach((c) => {
        const legsObj = Combination.of(
          uniqueBetsExceptBanker.map((b) => b.providerId),
          c
        );
        allLegs = allLegs.concat([...legsObj]);
      });

      //add bankers
      const uniqueBankerBets = _.uniqBy(
        enabledBets.filter((b) => b.banker),
        'providerId'
      );
      allLegs = allLegs.map((l) => [...l, ...uniqueBankerBets.map((b) => b.providerId)]);
    } else {
      // Mixed or bet builder only - treat bet builder groups as single bets
      const uniqueNormalBetsExceptBanker = _.uniqBy(
        normalBets.filter((b) => !b.banker),
        'providerId'
      );
      const uniqueBetBuilderBetsExceptBanker = Object.keys(betBuilderGroups).filter((betBuilderId) => {
        const groupBets = betBuilderGroups[betBuilderId];
        return !groupBets.some((b) => b.banker);
      });
      
      // Combine normal bets and bet builder groups (using providerId for bet builder groups)
      const uniqueBetsExceptBanker = [
        ...uniqueNormalBetsExceptBanker.map((b) => b.providerId),
        ...uniqueBetBuilderBetsExceptBanker.map((betBuilderId) => {
          const groupBets = betBuilderGroups[betBuilderId];
          return groupBets[0].providerId;
        })
      ];
      state.combinations.forEach((c) => {
        const legsObj = Combination.of(uniqueBetsExceptBanker, c);
        allLegs = allLegs.concat([...legsObj]);
      });

      //add bankers - treat bet builder groups as single bets
      const uniqueNormalBankerBets = _.uniqBy(
        normalBets.filter((b) => b.banker),
        'providerId'
      );
      const uniqueBetBuilderBankerBets = Object.keys(betBuilderGroups).filter((betBuilderId) => {
        const groupBets = betBuilderGroups[betBuilderId];
        return groupBets.some((b) => b.banker);
      });
      
      const bankerProviderIds = [
        ...uniqueNormalBankerBets.map((b) => b.providerId),
        ...uniqueBetBuilderBankerBets.map((betBuilderId) => {
          const groupBets = betBuilderGroups[betBuilderId];
          return groupBets[0].providerId;
        })
      ];
      allLegs = allLegs.map((l) => [...l, ...bankerProviderIds]);
    }

    //populateWithOutcomes
    Object.keys(outcomeMaps).map((providerId) => {
      const tempLegs = [];
      allLegs
        .filter((l) => l.includes(providerId))
        .map((l) => {
          outcomeMaps[providerId].map((o) => {
            tempLegs.push([...l.filter((ll) => ll !== providerId), o]);
          });
        });

      allLegs
        .filter((l) => l.includes(providerId) === false)
        .map((l) => {
          tempLegs.push(l);
        });

      allLegs = tempLegs;
    });

    const tax = (state.total * (state.taxRate / 100)).toFixed(2);
    const stake = state.total - tax;
    const stakePerBet = (stake / allLegs.length).toFixed(2);

    const maxWinningLegs = allLegs.filter((l) =>
      l.every(
        (outcome) => _.max(outcomeMaps[outcome.providerId].map((o) => o.odds)) === outcome.odds
      )
    );
    const winnings = maxWinningLegs.map((s) =>
      s.reduce((p, c) => p * c.odds, stake / allLegs.length).toFixed(2)
    );
    const maxWinning = winnings.reduce((p, c) => p + _.round(c, 2), 0).toFixed(2);

    return {
      tax: allLegs.length === 0 ? 0 : tax,
      stakePerBet: allLegs.length === 0 ? 0 : stakePerBet,
      totalOdds: null,
      maxWinning: maxWinning,
      numberOfBets: allLegs.length
    };
  } else if (enabledBets.length === mCount) {
    const nBets = Object.keys(outcomeMaps).reduce((p, c) => outcomeMaps[c].length * p, 1);
    const tax = (state.total * (state.taxRate / 100)).toFixed(2);
    const stake = state.total - tax;
    // Calculate winning using outcomeMaps which has correct odds for bet builder bets
    const winning = Object.keys(outcomeMaps).reduce((product, providerId) => {
      const outcomes = outcomeMaps[providerId];
      // For bet builder bets, use the calculated odds; for normal bets, multiply all outcomes
      const maxOdds = _.max(outcomes.map((o) => o.odds));
      return product * maxOdds;
    }, _.round(stake, 2)).toFixed(2);
    const totalOdds = (winning / _.round(stake, 2)).toFixed(2);
    return {
      tax: tax,
      stakePerBet: stake / nBets,
      totalOdds,
      maxWinning: winning,
      numberOfBets: 1
    };
  } else {
    const nBets = Object.keys(outcomeMaps).reduce((p, c) => outcomeMaps[c].length * p, 1);
    const tax = (state.total * (state.taxRate / 100)).toFixed(2);
    const sPerBet = (state.total - tax) / nBets;

    // If no bet builder bets, use original logic
    if (betBuilderBets.length === 0) {
      // Original multiple bets logic
      let bets = [enabledBets.map((p) => p.providerId)];
      Object.keys(outcomeMaps).map((providerId) => {
        const tempLegs = [];
        bets
          .filter((l) => l.includes(providerId))
          .map((l) => {
            outcomeMaps[providerId].map((o) => {
              tempLegs.push([...l.filter((ll) => ll !== providerId), o]);
            });
          });

        bets
          .filter((l) => l.includes(providerId) === false)
          .map((l) => {
            tempLegs.push(l);
          });

        bets = tempLegs;
      });

      const marketMaps = {};
      Object.values(outcomeMaps).forEach((b) =>
        b.forEach((o) => {
          if (marketMaps[o.providerId + '<>' + o.marketStringId] === undefined) {
            marketMaps[o.providerId + '<>' + o.marketStringId] = [];
          }
          marketMaps[o.providerId + '<>' + o.marketStringId].push(o);
        })
      );
      const maxWinningLegs = bets.filter((l) =>
        l.every(
          (outcome) =>
            _.max(
              marketMaps[outcome.providerId + '<>' + outcome.marketStringId].map((o) => o.odds)
            ) === outcome.odds
        )
      );
      const winnings = maxWinningLegs.map((s) => s.reduce((p, c) => p * c.odds, sPerBet).toFixed(2));
      const maxWinning = winnings.reduce((p, c) => p + _.round(c, 2), 0).toFixed(2);

      return {
        tax: tax,
        stakePerBet: sPerBet.toFixed(2),
        totalOdds: null,
        maxWinning: maxWinning,
        numberOfBets: nBets
      };
    }

    // Mixed or bet builder only - use updated logic
    // Start with providerIds from outcomeMaps (which includes bet builder groups as single entries)
    let bets = [Object.keys(outcomeMaps)];
    Object.keys(outcomeMaps).map((providerId) => {
      const tempLegs = [];
      bets
        .filter((l) => l.includes(providerId))
        .map((l) => {
          outcomeMaps[providerId].map((o) => {
            tempLegs.push([...l.filter((ll) => ll !== providerId), o]);
          });
        });

      bets
        .filter((l) => l.includes(providerId) === false)
        .map((l) => {
          tempLegs.push(l);
        });

      bets = tempLegs;
    });

    const marketMaps = {};
    Object.values(outcomeMaps).forEach((b) =>
      b.forEach((o) => {
        // For bet builder bets, use a unique key that includes betBuilderId to avoid conflicts
        // For normal bets, use the standard key
        const mapKey = o.isBetBuilder && o.betBuilderId !== undefined
          ? o.providerId + '<>' + o.marketStringId + '<>betBuilder' + o.betBuilderId
          : o.providerId + '<>' + o.marketStringId;
        if (marketMaps[mapKey] === undefined) {
          marketMaps[mapKey] = [];
        }
        marketMaps[mapKey].push(o);
      })
    );
    const maxWinningLegs = bets.filter((l) =>
      l.every(
        (outcome) => {
          // Use the same key logic for bet builder bets
          const mapKey = outcome.isBetBuilder && outcome.betBuilderId !== undefined
            ? outcome.providerId + '<>' + outcome.marketStringId + '<>betBuilder' + outcome.betBuilderId
            : outcome.providerId + '<>' + outcome.marketStringId;
          return marketMaps[mapKey] && 
            _.max(marketMaps[mapKey].map((o) => o.odds)) === outcome.odds;
        }
      )
    );
    // const winnings = maxWinningLegs.map((s) =>
    //   s
    //     .reduce(
    //       (p, c) =>
    //         p *
    //         _oddsTransformer.transform(
    //           new Date(),
    //           checkIfLiveEvent(c.match),
    //           false,
    //           c.marketId,
    //           new Date(c.match.gameDate),
    //           c.odds * state.customOddsMultiplier
    //         ),
    //       sPerBet
    //     )
    //     .toFixed(2)
    // );

    const winnings = maxWinningLegs.map((s) => s.reduce((p, c) => p * c.odds, sPerBet).toFixed(2));

    const maxWinning = winnings.reduce((p, c) => p + _.round(c, 2), 0).toFixed(2);

    return {
      tax: tax,
      stakePerBet: sPerBet.toFixed(2),
      totalOdds: null,
      maxWinning: maxWinning,
      numberOfBets: nBets
    };
  }
};

const saveBetSlipToLocalStorage = (state) => {
  localStorage.setItem('betSlipData', JSON.stringify(state));
};

export const betSlipSlice = createSlice({
  name: 'betSlip',
  initialState,
  reducers: {
    saveBetSlip: (state) => {
      saveBetSlipToLocalStorage(state);
    },
    setBetSlipSubmitting: (state, action) => {
      state.isBetSlipSubmitting = action.payload;
    },
    setResponseData: (state, action) => {
      state.responseData = action.payload;
    },
    setResultModalShow: (state, action) => {
      state.resultModalShow = action.payload;
    },
    loadBetSlip: (state, action) => {
      const updatedState = { ...state, ...action.payload };
      return {
        ...updatedState,
        ...calculateMaxWinningAndPrepareBetSlip(updatedState)
      };
    },
    addBetToBetSlip: (state, action) => {
      const payload = action.payload;
      let betBuilderId = payload.betBuilderId;
      
      // Check for conflicts: prevent mixing bet builder and normal bets for the same event
      // (This is a safety net - components should check before dispatching)
      if (!payload.isBetBuilder) {
        // Adding a normal bet - check if there's already a bet builder bet for this event
        const hasBetBuilderBet = state.bets.some(
          (b) => b.providerId === payload.providerId && b.isBetBuilder === true
        );
        if (hasBetBuilderBet) {
          // Return without adding - error should be shown by component
          return state;
        }
      } else {
        // Adding a bet builder bet - check if there's already a normal bet for this event
        const hasNormalBet = state.bets.some(
          (b) => b.providerId === payload.providerId && !b.isBetBuilder
        );
        if (hasNormalBet) {
          // Return without adding - error should be shown by component
          return state;
        }
      }
      
      // Check for duplicates
      // Normal bets: duplicate if same identifiers and both are normal bets (not bet builder)
      // Bet builder bets: duplicate if same identifiers, both are bet builder, and same betBuilderId
      const isDuplicate = state.bets.some(
        (b) => {
          // Must match basic identifiers
          if (b.providerId !== payload.providerId ||
              b.marketStringId !== payload.marketStringId ||
              b.outcomeId !== payload.outcomeId) {
            return false;
          }
          
          // Normal bet: duplicate only if existing bet is also a normal bet
          if (!payload.isBetBuilder) {
            return !b.isBetBuilder;
          }
          
          // Bet builder bet: duplicate if existing bet is also bet builder and has same betBuilderId
          // (betBuilderId is assigned in the modal before dispatching, but handle undefined case for safety)
          if (payload.isBetBuilder && b.isBetBuilder) {
            // If both have betBuilderId, they must match
            if (payload.betBuilderId !== undefined && b.betBuilderId !== undefined) {
              return payload.betBuilderId === b.betBuilderId;
            }
            // If neither has betBuilderId, they're duplicates (same selection being added)
            if (payload.betBuilderId === undefined && b.betBuilderId === undefined) {
              return true;
            }
            // If one has betBuilderId and other doesn't, they're different (different groups)
            return false;
          }
          
          return false;
        }
      );
      
      // If it's a duplicate of the same type, don't add it
      if (isDuplicate) {
        return state;
      }
      
      // If it's a bet builder bet and doesn't have a BetBuilderId, generate a unique one
      if (payload.isBetBuilder && !betBuilderId) {
        // Generate a new unique BetBuilderId
        const allBetBuilderIds = state.bets
          .filter((b) => b.isBetBuilder && b.betBuilderId !== undefined)
          .map((b) => b.betBuilderId);
        betBuilderId = allBetBuilderIds.length > 0 ? Math.max(...allBetBuilderIds) + 1 : 1;
      }
      
      const newState = {
        ...state,
        bets: [
          ...state.bets,
          {
            ...payload,
            enabled: true,
            banker: false,
            // Only set betBuilderId for bet builder bets
            ...(payload.isBetBuilder ? { betBuilderId: betBuilderId } : {})
          }
        ]
      };
      return { ...newState, ...calculateMaxWinningAndPrepareBetSlip(newState) };
    },
    removeBetFromBetSlip: (state, action) => {
      const { providerId, marketStringId, outcomeId } = action.payload;
      const filteredBets = state.bets.filter(
        (b) =>
          b.providerId !== providerId ||
          b.marketStringId !== marketStringId ||
          b.outcomeId !== outcomeId
      );
      const combinationRelatedBetCount = _.uniqBy(
        filteredBets.filter((b) => b.enabled && !b.banker),
        'providerId'
      ).length;
      const newState = {
        ...state,
        bets: filteredBets,
        combinations: [...state.combinations.filter((i) => i <= combinationRelatedBetCount)]
      };
      return { ...newState, ...calculateMaxWinningAndPrepareBetSlip(newState) };
    },
    removeBetBuilderGroup: (state, action) => {
      const { betBuilderId } = action.payload;
      const newState = {
        ...state,
        bets: state.bets.filter(
          (b) => !(b.isBetBuilder && b.betBuilderId === betBuilderId)
        )
      };
      return { ...newState, ...calculateMaxWinningAndPrepareBetSlip(newState) };
    },
    setBetSlipEnabled: (state, action) => {
      const { providerId, enabled } = action.payload;
      const bets = state.bets.map((b) => {
        return { ...b, enabled: providerId === b.providerId ? enabled : b.enabled };
      });
      const combinationRelatedBetCount = _.uniqBy(
        bets.filter((b) => b.enabled && !b.banker),
        'providerId'
      ).length;
      const newState = {
        ...state,
        bets: bets,
        combinations: [...state.combinations.filter((i) => i <= combinationRelatedBetCount)]
      };
      return { ...newState, ...calculateMaxWinningAndPrepareBetSlip(newState) };
    },
    setBetSlipBanker: (state, action) => {
      const { providerId, banker } = action.payload;
      const bets = state.bets.map((b) => ({
        ...b,
        banker: providerId === b.providerId ? banker : b.banker
      }));
      const combinationRelatedBetCount = _.uniqBy(
        bets.filter((b) => b.enabled && !b.banker),
        'providerId'
      ).length;
      const newState = {
        ...state,
        bets: bets,
        combinations: [...state.combinations.filter((i) => i <= combinationRelatedBetCount)]
      };
      return { ...newState, ...calculateMaxWinningAndPrepareBetSlip(newState) };
    },
    setBetSlipCombination: (state, action) => {
      const combination = action.payload;
      const include = state.combinations.includes(combination);
      const newState = {
        ...state,
        combinations: include
          ? state.combinations.filter((c) => c !== combination)
          : [...state.combinations, combination].sort()
      };
      return { ...newState, ...calculateMaxWinningAndPrepareBetSlip(newState) };
    },
    setCustomOddsMultiplier: (state, action) => {
      if (action.payload >= 0 && action.payload <= 2) {
        state.customOddsMultiplier = action.payload;
        return state;
      }
    },
    removeAllBetSlip: (state) => {
      localStorage.removeItem('betSlipData');
      return {
        ...state,
        outright: 0,
        total: state.defaultStake,
        bets: [],
        system: false,
        combinations: [],
        maxWinning: 0
      };
    },
    setTaxRate: (state, action) => {
      state.taxRate = action.payload;
    },
    setDefaultStake: (state, action) => {
      state.defaultStake = action.payload;
    },
    setTotal: (state, action) => {
      const newState = { ...state, total: action.payload };
      return { ...newState, ...calculateMaxWinningAndPrepareBetSlip(newState) };
    },
    setBetSlipSystem: (state, action) => {
      const value = action.payload;
      const newState = {
        ...state,
        system: value,
        combinations: value ? state.combinations : []
      };
      return { ...newState, ...calculateMaxWinningAndPrepareBetSlip(newState) };
    },
    updateBetSlipBetsOdds: (state, action) => {
      const { providerId, marketStringId, outcomeId, enabled, odds, match } = action.payload;
      const bets = state.bets.map((b) => {
        if (
          b.providerId === providerId &&
          b.marketStringId === marketStringId &&
          b.outcomeId === outcomeId
        ) {
          return { ...b, enabled, odds, match };
        }
        return b;
      });
      const newState = {
        ...state,
        bets: bets
      };
      return { ...newState, ...calculateMaxWinningAndPrepareBetSlip(newState) };
    },
    setBetBuilderEnabled: (state, action) => {
      state.betBuilder.isEnabled = action.payload;
    },
    addBetBuilderSelection: (state, action) => {
      const selection = action.payload;
      
      // Safety check: prevent adding bet builder selection if there are normal bets for this event
      const hasNormalBet = state.bets.some(
        (b) => b.providerId === selection.providerId && !b.isBetBuilder
      );
      if (hasNormalBet) {
        // Don't add selection - error should be shown by component
        return state;
      }
      
      console.log('Adding bet builder selection:', selection);
      console.log('Current selections:', state.betBuilder.selections);
      
      // One line per market: MyOdds prices only the first line of a market, so a new line
      // (e.g. Over 3.5 after Over 2.5) or a new outcome replaces the previous selection
      const existingIndex = state.betBuilder.selections.findIndex(
        (s) => s.providerId === selection.providerId && s.marketId === selection.marketId
      );
      
      console.log('Existing index:', existingIndex);
      
      if (existingIndex >= 0) {
        // Replace existing selection for the same market
        console.log('Replacing existing selection at index:', existingIndex);
        state.betBuilder.selections[existingIndex] = selection;
      } else {
        // Add new selection
        console.log('Adding new selection');
        state.betBuilder.selections.push(selection);
      }
      
      console.log('Updated selections:', state.betBuilder.selections);
    },
    removeBetBuilderSelection: (state, action) => {
      const { providerId, marketStringId } = action.payload;
      state.betBuilder.selections = state.betBuilder.selections.filter(
        (s) => !(s.providerId === providerId && s.marketStringId === marketStringId)
      );
      // Clear calculated odds when selections change
      if (state.betBuilder.selections.length === 0) {
        resetBetBuilderCalculation(state.betBuilder);
      }
    },
    clearBetBuilderSelections: (state) => {
      state.betBuilder.selections = [];
      resetBetBuilderCalculation(state.betBuilder);
    },
    // The server re-priced a custom bet group (PlaceBet B021); calculatedOdds is the MyOdds price
    updateBetBuilderGroupOdds: (state, action) => {
      const { betBuilderId, calculatedOdds } = action.payload;
      const newState = {
        ...state,
        bets: state.bets.map((b) =>
          b.isBetBuilder && b.betBuilderId === betBuilderId
            ? { ...b, betBuilderCalculatedOdds: calculatedOdds }
            : b
        )
      };
      return { ...newState, ...calculateMaxWinningAndPrepareBetSlip(newState) };
    },
    setBetBuilderModalShow: (state, action) => {
      state.betBuilder.showModal = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(login.fulfilled, (state, action) => {
        const { customer } = action.payload;
        state.taxRate = customer.customerLimitDto.taxRate ?? 0;
        const defaultStake =
          Math.max(
            customer.customerSettingsDto.defaultStake || 0,
            customer.customerLimitDto.minimumStakeLimit || 0
          ) || 10;
        state.total = defaultStake;
        state.defaultStake = defaultStake;
        state = {
          ...state,
          ...calculateMaxWinningAndPrepareBetSlip(state)
        };
      })
      .addCase(fetchUser.fulfilled, (state, action) => {
        if (action.payload) {
          const { loggedCustomer } = action.payload;
          state.taxRate = loggedCustomer.customer.customerLimitDto.taxRate ?? 0;
          const defaultStake =
            Math.max(
              loggedCustomer.customer.customerSettingsDto.defaultStake || 0,
              loggedCustomer.customer.customerLimitDto.minimumStakeLimit || 0
            ) || 10;
          state.total = defaultStake;
          state.defaultStake = defaultStake;
          state = {
            ...state,
            ...calculateMaxWinningAndPrepareBetSlip(state)
          };
        }
      })
      .addCase(getBetSlipMatches.fulfilled, (state, action) => {
        const events = action.payload;
        if (!events || events.length === 0) return;
        const betSlips = localStorage.getItem('betSlipData');
        const betSlipObject = JSON.parse(betSlips);
        if (betSlipObject?.bets) {
          betSlipObject.bets = betSlipObject.bets.filter((p) => {
            if (events.some((m) => m.gameNumber === p.providerId)) {
              p.odds = events
                .find((m) => m.gameNumber === p.providerId)
                .markets.find((m) => m.stringId === p.marketStringId)
                .outcomes.find((o) => o.id === p.outcomeId).odds;
              return true;
            } else {
              return false;
            }
          });
        }
        let newState = {
          ...state,
          ...betSlipObject
        };
        newState = {
          ...newState,
          ...calculateMaxWinningAndPrepareBetSlip(newState)
        };
        Object.keys(newState).forEach((k) => {
          state[k] = newState[k];
        });
      })
      .addCase(calculateBetBuilderOdds.pending, (state, action) => {
        state.betBuilder.requestId = action.meta.requestId;
        state.betBuilder.status = BET_BUILDER_STATUS.LOADING;
      })
      .addCase(calculateBetBuilderOdds.fulfilled, (state, action) => {
        // An older calculation must not overwrite the answer for the current selection
        if (state.betBuilder.requestId !== action.meta.requestId) return;
        if (!action.payload) {
          resetBetBuilderCalculation(state.betBuilder);
          return;
        }
        const { calculatedOdds, conflicts } = action.payload;
        const playable = calculatedOdds > 1;
        state.betBuilder.calculatedOdds = playable ? calculatedOdds : null;
        state.betBuilder.conflicts = conflicts;
        state.betBuilder.status = playable ? BET_BUILDER_STATUS.OK : BET_BUILDER_STATUS.NOT_COMBINABLE;
      })
      .addCase(calculateBetBuilderOdds.rejected, (state, action) => {
        if (state.betBuilder.requestId !== action.meta.requestId) return;
        state.betBuilder.calculatedOdds = null;
        state.betBuilder.conflicts = [];
        state.betBuilder.status = action.payload ?? BET_BUILDER_STATUS.UNAVAILABLE;
      });
  }
});

export const {
  saveBetSlip,
  loadBetSlip,
  addBetToBetSlip,
  removeBetFromBetSlip,
  removeBetBuilderGroup,
  setBetSlipEnabled,
  setBetSlipBanker,
  setBetSlipCombination,
  setCustomOddsMultiplier,
  removeAllBetSlip,
  setTaxRate,
  setDefaultStake,
  setTotal,
  setBetSlipSystem,
  updateBetSlipBetsOdds,
  setBetSlipSubmitting,
  setResponseData,
  setResultModalShow,
  setBetBuilderEnabled,
  addBetBuilderSelection,
  removeBetBuilderSelection,
  clearBetBuilderSelections,
  setBetBuilderModalShow,
  updateBetBuilderGroupOdds
} = betSlipSlice.actions;

export default betSlipSlice.reducer;
